import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router'
import { Star, CheckCircle, MapPin, X, Maximize2, Minimize2 } from 'lucide-react'
import { formatRating } from '~/lib/format'

interface MapPro {
  id: string
  companyName: string
  slug: string
  city: string
  ratingAvg: string | null
  reviewCount: number
  identityVerified: boolean
  latitude?: string | null
  longitude?: string | null
}

interface ProMapProps {
  pros: MapPro[]
  centerLat?: number
  centerLng?: number
  className?: string
}

/**
 * Interactive map using Leaflet (loaded from CDN).
 * Falls back to a static list if Leaflet fails to load.
 */
export function ProMap({ pros, centerLat = 48.8566, centerLng = 2.3522, className = '' }: ProMapProps) {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<any>(null)
  const [selectedPro, setSelectedPro] = useState<MapPro | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [leafletLoaded, setLeafletLoaded] = useState(false)

  // Load Leaflet from CDN
  useEffect(() => {
    if (typeof window === 'undefined') return

    // Check if already loaded
    if ((window as any).L) {
      setLeafletLoaded(true)
      return
    }

    // Load CSS
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
    document.head.appendChild(link)

    // Load JS
    const script = document.createElement('script')
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
    script.onload = () => setLeafletLoaded(true)
    script.onerror = () => {} // Fail silently
    document.head.appendChild(script)

    return () => {
      // Cleanup map on unmount
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [])

  // Initialize map when Leaflet is loaded
  useEffect(() => {
    if (!leafletLoaded || !mapRef.current || mapInstanceRef.current) return

    const L = (window as any).L
    if (!L) return

    const map = L.map(mapRef.current, {
      zoomControl: false,
      attributionControl: false,
    }).setView([centerLat, centerLng], 11)

    // Add zoom control to top-right
    L.control.zoom({ position: 'topright' }).addTo(map)

    // Tile layer — CartoDB Positron (clean, minimal style)
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      maxZoom: 19,
    }).addTo(map)

    // Add markers for pros with coordinates
    const markers: any[] = []
    const prosWithCoords = pros.filter((p) => p.latitude && p.longitude)

    prosWithCoords.forEach((pro) => {
      const lat = parseFloat(pro.latitude!)
      const lng = parseFloat(pro.longitude!)
      if (isNaN(lat) || isNaN(lng)) return

      // Custom marker icon
      const icon = L.divIcon({
        className: 'pro-marker',
        html: `<div style="
          background: #2563eb;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 13px;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          cursor: pointer;
        ">${pro.companyName[0]}</div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      })

      const marker = L.marker([lat, lng], { icon }).addTo(map)
      marker.on('click', () => setSelectedPro(pro))
      markers.push(marker)
    })

    // Fit bounds if we have markers
    if (markers.length > 1) {
      const group = L.featureGroup(markers)
      map.fitBounds(group.getBounds().pad(0.15))
    }

    mapInstanceRef.current = map

    // Force resize on next tick
    setTimeout(() => map.invalidateSize(), 100)
  }, [leafletLoaded, pros, centerLat, centerLng])

  // Resize on expand/collapse
  useEffect(() => {
    if (mapInstanceRef.current) {
      setTimeout(() => mapInstanceRef.current.invalidateSize(), 200)
    }
  }, [isExpanded])

  const prosWithCoords = pros.filter((p) => p.latitude && p.longitude)

  if (prosWithCoords.length === 0) return null

  return (
    <div className={`relative overflow-hidden rounded-2xl border border-border bg-white shadow-xs ${className}`}>
      {/* Map container */}
      <div
        ref={mapRef}
        className={`w-full transition-all duration-300 ${isExpanded ? 'h-[500px]' : 'h-72'}`}
        style={{ zIndex: 0 }}
      />

      {/* Expand/collapse */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="absolute left-3 top-3 z-10 flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 shadow-md hover:bg-slate-50"
      >
        {isExpanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
        {isExpanded ? 'Réduire' : 'Agrandir'}
      </button>

      {/* Pro count badge */}
      <div className="absolute right-3 top-3 z-10 rounded-lg bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 shadow-md">
        <MapPin className="mr-1 inline h-3.5 w-3.5 text-primary" />
        {prosWithCoords.length} pro{prosWithCoords.length > 1 ? 's' : ''} sur la carte
      </div>

      {/* Selected pro popup */}
      {selectedPro && (
        <div className="absolute bottom-3 left-3 right-3 z-10 animate-fade-in rounded-xl bg-white p-4 shadow-xl">
          <button
            type="button"
            onClick={() => setSelectedPro(null)}
            className="absolute right-2 top-2 rounded-full p-1 text-slate-400 hover:bg-slate-100"
          >
            <X className="h-4 w-4" />
          </button>
          <Link to={`/pro/${selectedPro.slug}`} className="flex items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-lg font-bold text-primary">
              {selectedPro.companyName[0]}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <h4 className="truncate text-sm font-semibold text-slate-900">{selectedPro.companyName}</h4>
                {selectedPro.identityVerified && (
                  <CheckCircle className="h-3.5 w-3.5 shrink-0 text-primary" />
                )}
              </div>
              <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                {(selectedPro.reviewCount ?? 0) > 0 ? (
                  <span className="flex items-center gap-0.5">
                    <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                    {formatRating(selectedPro.ratingAvg)}
                    <span className="text-slate-400">({selectedPro.reviewCount})</span>
                  </span>
                ) : (
                  <span className="text-blue-600">Nouveau</span>
                )}
                <span>·</span>
                <span>{selectedPro.city}</span>
              </div>
            </div>
            <span className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white">
              Voir
            </span>
          </Link>
        </div>
      )}
    </div>
  )
}
